<template>
  <div class="metadata-table-layout">
    <table class="metadata-custom-table"
           :class="[{edit: tableMode === 'edit', view: tableMode === 'view', compact: showCompactView, hideStoreMeta: hideStoreMeta, hideWordsToUse: !showWordsToUse}, classes]">
      <thead>
      <tr>
        <td class="medium">Title</td>
        <td class="medium">Subtitle</td>
        <td>Keywords</td>
      </tr>
      </thead>
      <tbody class="tbody">
      <template v-for="(row, key) in computedMetaData">
        <tr class="trow locale-row" v-if="!showCompactView">
          <td class="td-item locale-name" colspan="3">
            <dynamic-image
                :key="row.locale_code"
                classes="country-flag"
                :width="18"
                :height="14"
                :size="32"
                :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
            {{ row?.locale_name + ' (' + row.locale_code + ')'}}
          </td>
        </tr>
        <tr class="trow middle-row">
          <td class="medium">
            <custom-tooltip
                v-if="showCompactView"
                classes="country-flag"
                :class="{edit: tableMode === 'edit'}"
                :style="{width: 'max-content', padding: '12px 16px', fontSize: '16px'}"
                direction-horizontal="right">
              <template v-slot:slotTrigger>
                <dynamic-image
                    :key="row.locale_code"
                    :width="12"
                    :height="10"
                    :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
              </template>
              <template v-slot:slotContent>
                <dynamic-image
                    v-if="showCompactView"
                    :key="row.locale_code"
                    :width="14"
                    :height="12"
                    :size="32"
                    :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
                {{ row?.locale_name + ' (' + row.locale_code + ')'}}
              </template>
            </custom-tooltip>

            <editable-table-cell :table-mode="tableMode"
                                 :initial-data="row?.title?.text"
                                 :store-data="hideStoreMeta ? null : storeData?.metas[key]?.title?.text"
                                 :duplicated-words="row?.duplicated"
                                 :text-max-length="30"
                                 view-mode-placeholder="No title"
                                 edit-mode-placeholder="Enter the title"
                                 @turn-on-edit-mode="turnOnEditMode"
                                 @editable-data-changed="editableDataChanged(key, 'title', $event)">
              <template #rightOfInput>
                <div class="display-flex cursor-pointer" v-if="hasBetaCookie()">
                  <AiIcon class="ai-icon--pointer" @click="openAiAssistModal(row, 'title')"/>
                </div>
              </template>
            </editable-table-cell>
          </td>
          <td class="no-border medium">
            <custom-tooltip
                v-if="showCompactView"
                classes="country-flag"
                :class="{edit: tableMode === 'edit'}"
                :style="{width: 'max-content', padding: '12px 16px', fontSize: '16px'}"
                direction-horizontal="right">
              <template v-slot:slotTrigger>
                <dynamic-image
                    :key="row.locale_code"
                    :width="12"
                    :height="10"
                    :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
              </template>
              <template v-slot:slotContent>
                <dynamic-image
                    v-if="showCompactView"
                    :key="row.locale_code"
                    :width="12"
                    :height="10"
                    :size="32"
                    :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
                {{ row?.locale_name + ' (' + row.locale_code + ')'}}
              </template>
            </custom-tooltip>
            <editable-table-cell :table-mode="tableMode"
                                 :initial-data="row?.sub_title?.text"
                                 :store-data="hideStoreMeta ? null : storeData?.metas[key]?.sub_title?.text"
                                 :duplicated-words="row?.duplicated"
                                 :text-max-length="30"
                                 view-mode-placeholder="No subtitle"
                                 edit-mode-placeholder="Enter the subtitle"
                                 @turn-on-edit-mode="turnOnEditMode"
                                 @editable-data-changed="editableDataChanged(key, 'sub_title', $event)">
              <template #rightOfInput>
                <div class="display-flex cursor-pointer" v-if="hasBetaCookie()">
                  <AiIcon class="ai-icon--pointer" @click="openAiAssistModal(row, 'sub_title')"/>
                </div>
              </template>
            </editable-table-cell>
          </td>
          <td rowspan="2" class="border-left medium align-top words-block">
            <editable-table-cell :table-mode="tableMode"
                                 :cell-type="'keyword-cell'"
                                 :text-max-length="100"
                                 view-mode-placeholder="No keywords"
                                 edit-mode-placeholder="Enter keywords"
                                 :duplicated-words="row?.duplicated"
                                 :initial-data="null"
                                 :initial-list-data="row?.keywords"
                                 :store-data="hideStoreMeta ? null : storeData?.metas[key]?.keywords.join(',')"
                                 @turn-on-edit-mode="turnOnEditMode"
                                 @editable-data-changed="editableDataChanged(key, 'keywords', $event)">
              <template v-slot:startOfCell>
                <custom-tooltip
                    v-if="showCompactView"
                    classes="country-flag"
                    :class="{edit: tableMode === 'edit'}"
                    :style="{width: 'max-content', padding: '12px 16px', fontSize: '16px'}"
                    direction-horizontal="right">
                  <template v-slot:slotTrigger>
                    <dynamic-image
                        :key="row.locale_code"
                        :width="12"
                        :height="10"
                        :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
                  </template>
                  <template v-slot:slotContent>
                    <dynamic-image
                        v-if="showCompactView"
                        :key="row.locale_code"
                        :width="12"
                        :height="10"
                        :size="32"
                        :country-code="getLocaleCountryCode(row.locale_code)?.countryCode"/>
                    {{ row?.locale_name + ' (' + row.locale_code + ')'}}
                  </template>
                </custom-tooltip>
              </template>
              <template v-slot:storeDataAfter>
                <custom-tooltip :style="{minWidth: '30px'}"
                                class="store-keywords-tooltip"
                                @click="showStoreKeywordsModal">
                  <template v-slot:slotTrigger>
                    <svg-icon icon="pencil-icon"/>
                  </template>
                  <template v-slot:slotContent>
                    Edit Store Keywords
                  </template>
                </custom-tooltip>
              </template>
              <template v-slot:storeDataPlaceholder v-if="!hideStoreMeta">
                <div class="enter-keywords-fields" v-tooltip="{text: ' The system doesn\'t allow us to get Keywords from AppStore. Please add keywords manually to avoid duplicates.', position: 'top'}">
                  <img src="@/assets/images/icons/app_store_gray.svg"
                       alt="Store icon"
                       class="store-icon"
                       width="18"
                       height="18">
                  <span @click="showStoreKeywordsModal" class="link">Enter keywords fields</span> data from App Store Console
                </div>
              </template>
            </editable-table-cell>
          </td>
        </tr>
        <tr class="trow large-row">
          <td class="medium">
            <editable-table-cell :table-mode="tableMode"
                                 v-tooltip="{text: 'Words to use in title', position: 'top', classes: [ 'tooltip-words']}"
                                 classes="words-to-use-editable"
                                 :cell-type="'keyword-cell'"
                                 :show-length-limits="false"
                                 :text-max-length="10000"
                                 edit-mode-placeholder="Enter words"
                                 :initial-data="null"
                                 :initial-list-data="wordsToUseProcessed(row?.words_to_use_in_title, row?.title?.used_words)"
                                 :duplicated-words="row?.duplicated"
                                 :no-highlight="true"
                                 :words-to-use="row?.words_to_use_in_title"
                                 :used-words-data="row?.title.used_words"
                                 @turn-on-edit-mode="turnOnEditMode"
                                 @editable-data-changed="editableDataChanged(key, 'words_to_use_in_title', $event)">
            </editable-table-cell>
          </td>
          <td class="no-border medium">
            <editable-table-cell :table-mode="tableMode"
                                 v-tooltip="{text: 'Words to use in subtitle', position: 'top', classes: [ 'tooltip-words']}"
                                 classes="words-to-use-editable"
                                 :cell-type="'keyword-cell'"
                                 :show-length-limits="false"
                                 :text-max-length="10000"
                                 edit-mode-placeholder="Enter words"
                                 :initial-data="null"
                                 :initial-list-data="wordsToUseProcessed(row?.words_to_use_in_subtitle, row?.sub_title?.used_words)"
                                 :duplicated-words="row?.duplicated"
                                 :no-highlight="true"
                                 :used-words-data="row?.sub_title.used_words"
                                 @turn-on-edit-mode="turnOnEditMode"
                                 @editable-data-changed="editableDataChanged(key, 'words_to_use_in_subtitle', $event)">
            </editable-table-cell>
          </td>
        </tr>
      </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import EditableTableCell from "@/views/MetaEditor/components/EditableTableCell/index.vue";
import SvgIcon from "@/components/UI/SvgIcon/index.vue";
import {getLocaleCountryCode} from "@/configs/locales-flags";
import AiIcon from "@/components/icons/AiIcon.vue";
import MetaEditorAiAssistantModal from "@/components/Modals/MetaEditorAiAssistantModal.vue";
import {closeGlobalModal, hasBetaCookie, openGlobalModal} from "@/utils/utils";
import {h} from "vue";
import {mapGetters} from "vuex";

export default {
  name: "MetaEditorTable",
  components: {
    AiIcon,
    SvgIcon,
    EditableTableCell
  },
  emits: [
    'editable-table-value-changed',
    'turn-on-edit-mode',
    'show-store-keywords-modal',
  ],
  props: {
    selectedLocales: {
      type: Array,
      default: [],
    },
    countryCode: {
      type: String,
      default: '',
    },
    tableMode: {
      type: String,
      default: 'view'
    },
    tableData: {
      type: Object,
    },
    storeData: {
      type: Object,
    },
    classes: {
      type: String,
    },
    showCompactView: {
      type: Boolean,
      default: false,
    },
    hideStoreMeta: {
      type: Boolean,
      default: false,
    },
    showWordsToUse: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      searchInput: '',
      localTableData: {},
    }
  },
  mounted() {
    this.localTableData = {...this.tableData};
  },
  methods: {
    hasBetaCookie,
    openAiAssistModal(row, type) {
      openGlobalModal(h(MetaEditorAiAssistantModal, {
        app: this.currentApp,
        localeCode: row.locale_code,
        localeName: row.locale_name,
        countryCode: this.countryCode,
        type: type,
        initWords: type === 'title' ? row.words_to_use_in_title : row.words_to_use_in_subtitle,
        onApply: (variant) => {
          row[type].text = variant;
          this.editableDataChanged(row.locale_code, type, variant)
          closeGlobalModal()
        }
      }))
    },
    getLocaleCountryCode,
    showStoreKeywordsModal() {
      this.$emit('show-store-keywords-modal');
    },
    editableDataChanged(localeCode, itemType, newValue) {
      this.$emit('editable-table-value-changed', {
        localeCode,
        itemType,
        newValue
      });
    },
    turnOnEditMode() {
      this.$emit('turn-on-edit-mode');
    },
    wordsToUseProcessed(wordsToUse, usedWordsData) {
      let usedWordsDataLowerMap = {};
      usedWordsData?.forEach(item => {
        usedWordsDataLowerMap[item.toLowerCase()] = item;
      });

      wordsToUse = [...wordsToUse];

      return [...new Set(wordsToUse.filter(item => item && !usedWordsDataLowerMap?.[item.toLowerCase()]))];
    },
  },
  computed: {
    ...mapGetters([
        'currentApp'
    ]),
    computedMetaData() {
      if (this.selectedLocales.length === 0) {
        return this.localTableData?.metas;
      }
      if (this.localTableData?.metas === undefined) {
        return [];
      }

      let meta = {};
      this.selectedLocales.forEach((code) => {
        if (this.localTableData?.metas[code]) {
          meta[code] = this.localTableData?.metas[code];
        } else if (this.storeData?.metas[code]) {
          meta[code] = this.storeData.metas[code];
        }
      });

      return meta;
    },
  },
  watch: {
    tableData(newVal, oldVal) {
      this.localTableData = {...newVal};
    },
  },
}
</script>

<style lang="scss" src="./styles.scss"></style>